import { useMemo } from 'react';
import { useGetPurchaseOrderActivity } from '@/services/apis/queries/purchase-order/use-get-purchase-order-activity';
import { isPurchaseOrderTimelineActivity } from '../config/purchase-order-activity-config';
import { usePurchaseOrderActivityTimelineDocument } from './use-purchase-order-activity-timeline-document';

type UsePurchaseOrderActivityTimelineParams = {
  purchaseOrderId: string;
};

export function usePurchaseOrderActivityTimeline({
  purchaseOrderId,
}: UsePurchaseOrderActivityTimelineParams) {
  const { data } = useGetPurchaseOrderActivity({ purchaseOrderId });

  const enrichedActivities = useMemo(() => {
    const metadataByCorrelationId = new Map<string, Record<string, unknown>>();

    for (const activity of data) {
      if (!(activity.correlationId && activity.metadata)) {
        continue;
      }

      const existingMetadata =
        metadataByCorrelationId.get(activity.correlationId) ?? {};
      metadataByCorrelationId.set(activity.correlationId, {
        ...existingMetadata,
        ...activity.metadata,
      });
    }

    return data.map((activity) => {
      if (!activity.correlationId) {
        return activity;
      }

      const sharedMetadata = metadataByCorrelationId.get(
        activity.correlationId,
      );
      if (!sharedMetadata) {
        return activity;
      }

      return {
        ...activity,
        metadata: {
          ...sharedMetadata,
          ...(activity.metadata ?? {}),
        },
      };
    });
  }, [data]);

  const timelineActivities = useMemo(
    () => enrichedActivities.filter(isPurchaseOrderTimelineActivity),
    [enrichedActivities],
  );

  const { documentUrlByName } = usePurchaseOrderActivityTimelineDocument({
    purchaseOrderId,
  });

  return {
    documentUrlByName,
    timelineActivities,
  };
}
